// Term — the one way this page explains itself.
//
// Wraps any label in a definition from glossary.js. Hover or focus on a
// pointer; tap on touch; Escape or an outside click closes it. The trigger is
// a real <button>, so the whole page is reachable and readable by keyboard —
// a definition nobody can get to is not documentation.
//
// POSITIONED WITH position: fixed AND A MEASUREMENT, not with pure CSS. This
// page is a dense grid whose columns are 92px wide at the right-hand edge; a
// CSS-only popover would be clipped by the first card it opened inside, and
// the terms most in need of explanation are the ones in the narrowest cells.
// So the panel measures itself on open and flips left or up when it would
// leave the viewport.
//
// It renders NOTHING when a key is missing rather than throwing or showing an
// empty bubble — a term added to the UI before its definition should look
// undecorated, not broken.

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { GLOSSARY } from './glossary';

const GAP = 8;
const WIDTH = 330;

export default function Term({ k, children, className = '', as = 'span' }) {
  const entry = GLOSSARY[k];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const id = useId();

  const close = useCallback(() => setOpen(false), []);

  // Measure after paint, so the panel's real height is known before it is
  // placed. Placing first and correcting later makes it visibly jump.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const h = panelRef.current?.offsetHeight || 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = t.left;
    if (left + WIDTH + GAP > vw) left = Math.max(GAP, vw - WIDTH - GAP);

    let top = t.bottom + GAP;
    let flipped = false;
    if (top + h + GAP > vh && t.top - h - GAP > 0) {
      top = t.top - h - GAP;
      flipped = true;
    }
    setPos({ top, left, flipped });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { close(); triggerRef.current?.focus(); } };
    const onDown = (e) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) close();
    };
    // Any scroll invalidates a fixed-position measurement, and re-measuring on
    // every frame of a long page is worse than simply closing.
    const onScroll = () => close();
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, close]);

  if (!entry) return <>{children}</>;

  const Tag = as;

  return (
    <Tag className={`termWrap ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className="termTrigger"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => { if (!panelRef.current?.contains(e.relatedTarget)) close(); }}
      >
        {children}
      </button>

      {open && (
        <div
          ref={panelRef}
          id={id}
          role="tooltip"
          className={`termPanel${pos?.flipped ? ' isUp' : ''}`}
          style={pos ? { top: pos.top, left: pos.left } : { opacity: 0, top: 0, left: 0 }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={close}
        >
          <p className="termName">{entry.term}</p>
          <p className="termWhat">{entry.what}</p>
          {entry.from && <p className="termFrom">{entry.from}</p>}
          {entry.catch && <p className="termCatch">{entry.catch}</p>}
        </div>
      )}
    </Tag>
  );
}
